/**
 * Figures printed in the Commitment Agreement summary.
 *
 * These were inline in PrintSchedule and both misreported the sample
 * published on 2026-08-25, so they live here with tests around them.
 */
import { snapDisplayZero } from "@/lib/money";

export type MarginDirection = "gained" | "reduced" | "unchanged";

export interface MarginChange {
  /** Unsigned magnitude of the change, in dollars. */
  delta: number;
  direction: MarginDirection;
  /** Tile label matching the direction, so a decrease never reads as a gain. */
  label: string;
}

const MARGIN_LABELS: Record<MarginDirection, string> = {
  gained: "Margin Gained",
  reduced: "Margin Reduced",
  unchanged: "Margin Change",
};

/**
 * Compare the Family Financing Margin™ with and without lump sums.
 *
 * Lump sums retire the policy loan sooner, which legitimately leaves fewer
 * periods for the discipline-rate overage to accrue — so the margin often
 * FALLS. Reporting that as a clamped "$0.00 gained" hid a real decrease.
 */
export function summariseMarginChange(base: number, withLumpSums: number): MarginChange {
  const signed = snapDisplayZero(withLumpSums - base);
  const direction: MarginDirection =
    signed > 0 ? "gained" : signed < 0 ? "reduced" : "unchanged";

  return { delta: Math.abs(signed), direction, label: MARGIN_LABELS[direction] };
}

/**
 * The last payment of the schedule the document actually prints.
 *
 * The summary previously quoted the solver's own estimate, which is derived
 * independently of the rendered rows and disagreed with them in the sample
 * ($487.67 in the summary vs $499.88 in the schedule). Quoting the schedule
 * itself makes the two incapable of diverging.
 */
export function finalScheduledPayment(
  payments: ReadonlyArray<{ amount: number }>,
): number | null {
  if (payments.length === 0) return null;
  return payments[payments.length - 1].amount;
}
